"use strict";
/**
 * Administrator page + report access (deny-lists): the pure policy.
 *
 * A super_admin can withhold specific commission pages and reports from an
 * `administrator` without demoting them: the tenant-level commission views
 * (Commissions tab, Commission Settings, dashboard commission sections) and any
 * report, while loan-level figures, loan book totals and every other page stay
 * visible.
 *
 * Stored on `users/{uid}` as `deniedAdminPages` / `deniedAdminReports` (written
 * only by updateBrokerAccessPermissions, and only while the tenant flag
 * `featureFlags.admin_access_controls` is on), mirrored to token claims of the
 * same names by onUserWrite.
 *
 * Deny-lists, not allow-lists, so an absent field is "unrestricted" and every
 * existing user is byte-identical. Both lists bind ONLY on role
 * `administrator`: after a role change the fields are inert.
 *
 * This file has NO runtime dependencies so the client app can import it
 * (`@valutten/shared-utils/adminAccess`); the callable refusals that throw live
 * in adminAccess.ts.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ADMIN_DENIABLE_REPORT_SLUGS = exports.ADMIN_PAGE_SLUGS = void 0;
exports.parseDenyListClaim = parseDenyListClaim;
exports.adminPageDenied = adminPageDenied;
exports.adminReportDenied = adminReportDenied;
exports.ADMIN_PAGE_SLUGS = ['commissions', 'commission-settings', 'dashboard-commissions'];
/**
 * Reports an administrator can be denied. The 12 broker-grantable reports
 * except `commission-settings` (a broker-only view; the administrator surface is
 * the `commission-settings` PAGE), the 3 admin-tier valuation reports, and the
 * two admin-only broker-split reports.
 */
exports.ADMIN_DENIABLE_REPORT_SLUGS = [
    'settlement-age',
    'clawbacks',
    'arrears',
    'discharged-loans',
    'loans',
    'trail-drop-off',
    'book-health',
    'customer-portfolio',
    'refinance-intelligence',
    'acquired-books',
    'approved-vs-settled',
    'book-trend',
    'trail-book-valuation',
    'trail-forecast',
    'rcti-cashflow',
    'broker-loan-books',
    'recludo',
];
/**
 * Normalise a raw claim / doc value into a deny-list. Absent -> undefined
 * (unrestricted). A present value that is not a string array is corruption: the
 * producer only ever writes a non-empty string array or deletes the key, so a
 * restriction we cannot read FAILS CLOSED to the full list rather than
 * resolving to "no restriction".
 */
function parseDenyListClaim(raw, fullList) {
    if (raw === undefined || raw === null)
        return undefined;
    if (!Array.isArray(raw) || !raw.every((s) => typeof s === 'string'))
        return [...fullList];
    return raw.length > 0 ? raw : undefined;
}
function adminPageDenied(ctx, page) {
    if (ctx?.role !== 'administrator')
        return false;
    return Boolean(parseDenyListClaim(ctx.deniedAdminPages, exports.ADMIN_PAGE_SLUGS)?.includes(page));
}
function adminReportDenied(ctx, report) {
    if (ctx?.role !== 'administrator')
        return false;
    return Boolean(parseDenyListClaim(ctx.deniedAdminReports, exports.ADMIN_DENIABLE_REPORT_SLUGS)?.includes(report));
}
